import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getStaleTime, QUERY_TYPES } from '@/config/reactQuery'
import subscriptionService from '@/libs/services/subscriptions'

export const useSubscriptions = () => {
  const queryClient = useQueryClient()

  const {
    data: tiers = [],
    isLoading: isLoadingTiers,
    error: tiersError,
  } = useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: () => subscriptionService.getTiers().then(res => res.data),
    staleTime: getStaleTime(QUERY_TYPES.SUBSCRIPTION_TIERS),
  })

  const {
    data: cycles = [],
    isLoading: isLoadingCycles,
    error: cyclesError,
  } = useQuery({
    queryKey: ['subscription-cycles'],
    queryFn: () => subscriptionService.getCycles().then(res => res.data),
    staleTime: getStaleTime(QUERY_TYPES.SUBSCRIPTION_CYCLES),
  })

  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      const res = await subscriptionService.getSubscriptionsV2([
        'ACTIVE',
        'EXPIRED',
        'CANCELED',
        'PAYMENT_PENDING',
        'PAYMENT_FAILED',
      ])
      return res.data?.data ?? res.data ?? []
    },
    staleTime: getStaleTime(QUERY_TYPES.SUBSCRIPTIONS),
  })

  const organizationSubscriptions = Array.isArray(subscriptionsData) ? subscriptionsData : []
  const currentSubscription =
    organizationSubscriptions.find(s => s.state === 'ACTIVE' || s.state === 'PAYMENT_PENDING') ||
    null

  const calculatePriceMutation = useMutation({
    mutationFn: ({ tierId, cycleId }) =>
      subscriptionService.calculateTierPrice(tierId, cycleId).then(res => res.data),
    onError: error => {
      console.error('Error calculating price:', error)
    },
  })

  const purchaseSubscriptionMutation = useMutation({
    mutationFn: ({ tierId, cycleId }) =>
      subscriptionService.purchaseSubscriptionTier(tierId, cycleId).then(res => res.data),
    onSuccess: async () => {
      // Invalidate and refetch subscriptions and products after successful purchase
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
          queryClient.invalidateQueries({ queryKey: ['products'] }),
          queryClient.invalidateQueries({ queryKey: ['productsStats'] }),
        ])
      } catch (error) {
        console.error('Error invalidating queries after subscription purchase:', error)
      }
    },
    onError: error => {
      console.error('Error purchasing subscription:', error)
    },
  })

  const loading = isLoadingTiers || isLoadingCycles || isLoadingSubscriptions
  const error = tiersError || cyclesError || subscriptionsError

  return {
    tiers,
    cycles,
    organizationSubscriptions,
    currentSubscription,
    loading,
    error,
    calculatePrice: calculatePriceMutation.mutate,
    isCalculatingPrice: calculatePriceMutation.isPending,
    purchaseSubscription: purchaseSubscriptionMutation.mutate,
    isPurchasing: purchaseSubscriptionMutation.isPending,
  }
}
